// Application Components
import React, { useState, useEffect } from 'react'
import parse from 'html-react-parser'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionForm } from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { Form, RadioButtonGroup, RadioButton } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const LicensedPracticalNursingExaminationFunctionSuitabilityForm = ({
    instance,
}) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const fields = settingsData?.['form']?.['fields']

    //////// Search Params

    //////// Methods

    // Parse Functional Suitability One (1) Label
    const funcSuitOneLabel = () => {
        const menu_item_label = `<p>Have you graduated, or will you graduate from one of the following?</p><ul><li>A Florida approved nursing education program as defined in section 464.003, Florida Statutes.</li><li>An Accreditation Commission for Education in Nursing (ACEN) or Commission on Collegiate Nursing Education (CCNE) accredited nursing program that has been issued a National Council Licensure Examination (NCLEX) code by National Council of State Boards of Nursing (NCSBN).</li><li>A nursing education program that has been approved or recognized by the jurisdiction in which it is based and that has been issued an NCLEX code by NCSBN.</li><li>A military nursing education program that has been issued an NCLEX code by NCSBN.</li><li>A generic Master of Science in Nursing (MSN) or higher program that has been issued an NCLEX code by NCSBN.</li><li>A non-NCSBN jurisdiction or international education program that the Board of Nursing determines is equivalent to an approved program.</li><li>Practical Nurse examination based on practical nurse equivalency (PNEQ) – Applicants who have successfully completed courses equivalent to practical nursing education in a registered nursing program.</li></ul>`
        if (menu_item_label) {
            const label = parse(`${menu_item_label}`)
            if (label) {
                return label
            } else {
                return null
            }
        } else {
            return null
        }
    }

    // Parse Functional Suitability Three (3) Label
    const funcSuitThreeLabel = () => {
        const menu_item_label = `<p>Do you hold an active Nurse Licensure Compact (NLC) multistate license in another state AND ARE NOT changing your primary state of residency to Florida?</p><p>If you have an active NLC license but you are changing your primary state of residence to Florida OR if you do not have a Nurse Compact multistate license in another state select "No".</p>`
        if (menu_item_label) {
            const label = parse(`${menu_item_label}`)
            if (label) {
                return label
            } else {
                return null
            }
        } else {
            return null
        }
    }

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                FuncSuitOne: string()
                    .matches(
                        /^Y$/gm,
                        'You do not currently meet the qualifications to apply for this license. Please review the general requirements for this license type at the Florida Board of Nursing website.'
                    )
                    .required(
                        'This field is required to be entered or selected.'
                    ),
                FuncSuitTwo: string()
                    .matches(
                        /^N$/gm,
                        'You do not currently meet the qualifications to apply for this license. Please review the general requirements for this license type at the Florida Board of Nursing website.'
                    )
                    .required(
                        'This field is required to be entered or selected.'
                    ),
                FuncSuitThree: string()
                    .matches(
                        /^N$/gm,
                        'You do not currently meet the qualifications to apply for this license. Please review the general requirements for this license type at the Florida Board of Nursing website.'
                    )
                    .required(
                        'This field is required to be entered or selected.'
                    ),
            })
            schema.valid = {
                FuncSuitOne: false,
                FuncSuitOneErrMsg: '',
                FuncSuitTwo: false,
                FuncSuitTwoErrMsg: '',
                FuncSuitThree: false,
                FuncSuitThreeErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Data Of All Fields
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }

        //// Start Watson Assistant Journey

        if (e?.target?.name === 'FuncSuitOne' && e?.target?.value === 'N') {
            instance.tours.startTour(
                'Registered Nursing Endorsement Function Suitability Passed NCLEX No'
            )
        } else if (
            e?.target?.name === 'FuncSuitTwo' &&
            e?.target?.value === 'Y'
        ) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Function Suitability Passed NCLEX Not Moving to Florida'
            )
        } else if (
            e?.target?.name === 'FuncSuitThree' &&
            e?.target?.value === 'Y'
        ) {
            instance.tours.startTour(
                'Registered Nursing Endorsement Function Suitability Passed NCLEX Not Moving to Florida'
            )
        } else {
            instance.tours.endTour()
        }

        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Functional Suitability: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Functional Suitability: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Functional Suitability: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Functional Suitability Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FuncSuitOne
                                ? 'extra-text-required invalid'
                                : 'extra-text-required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FuncSuitOne
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FuncSuitOne
                        }
                        legendText={funcSuitOneLabel()}
                        name="FuncSuitOne"
                        invalid={formsData?.[digital_name]?.errors?.FuncSuitOne}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FuncSuitOneErrMsg
                        }
                    >
                        <RadioButton
                            id="FuncSuitOneYes"
                            name="FuncSuitOneYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="FuncSuitOneNo"
                            name="FuncSuitOneNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FuncSuitTwo
                                ? 'required invalid'
                                : 'required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FuncSuitTwo
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FuncSuitTwo
                        }
                        legendText="Have you ever been licensed by examination as an RN in another U.S. state/territory?"
                        name="FuncSuitTwo"
                        invalid={formsData?.[digital_name]?.errors?.FuncSuitTwo}
                        invalidText={
                            formsData?.[digital_name]?.errors?.FuncSuitTwoErrMsg
                        }
                    >
                        <RadioButton
                            id="FuncSuitTwoYes"
                            name="FuncSuitTwoYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="FuncSuitTwoNo"
                            name="FuncSuitTwoNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <RadioButtonGroup
                        className={
                            formsData?.[digital_name]?.errors?.FuncSuitThree
                                ? 'extra-text-required invalid'
                                : 'extra-text-required'
                        }
                        defaultSelected={
                            formsData?.[digital_name]?.values?.FuncSuitThree
                        }
                        valueSelected={
                            formsData?.[digital_name]?.values?.FuncSuitThree
                        }
                        legendText={funcSuitThreeLabel()}
                        name="FuncSuitThree"
                        invalid={
                            formsData?.[digital_name]?.errors?.FuncSuitThree
                        }
                        invalidText={
                            formsData?.[digital_name]?.errors
                                ?.FuncSuitThreeErrMsg
                        }
                    >
                        <RadioButton
                            id="FuncSuitThreeYes"
                            name="FuncSuitThreeYes"
                            labelText="Yes"
                            value="Y"
                            type="radio"
                            onClick={handleData}
                        />
                        <RadioButton
                            id="FuncSuitThreeNo"
                            name="FuncSuitThreeNo"
                            labelText="No"
                            value="N"
                            type="radio"
                            onClick={handleData}
                        />
                    </RadioButtonGroup>

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default LicensedPracticalNursingExaminationFunctionSuitabilityForm
